import { useFormikContext } from 'formik';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    GetMessageTemplateSettingsDocument,
    TemplateUseCases,
    useDisablePushMutation,
    useEnablePushMutation,
    useGetMessageTemplateSettingsQuery,
    useGetSingleSignInTokenMutation,
    useToggleBiometricMutation,
    useUpdateMessageTemplateSettingsMutation,
} from '../../../api';
import { useAlert } from '../../../components/AlertProvider';
import { useFlutter } from '../../../components/FlutterProvider';
import { useLoadingController } from '../../../components/LoadingProvider';
import { FormValues } from '../index';

const useBiometric = () => {
    const { t } = useTranslation(['settingsPage']);
    const { deviceFingerPrint: deviceFingerprint, checkBiometric } = useFlutter();
    const { show } = useAlert();

    // get values from formik
    const { values, setFieldValue } = useFormikContext<FormValues>();
    const { biometric: value } = values;

    // get mutations
    const [toggleBiometric, { loading: toggleLoading }] = useToggleBiometricMutation();
    const [getToken, { loading: getTokenLoading }] = useGetSingleSignInTokenMutation();

    // track latest values
    const previousValueRef = useRef(value);

    useLoadingController(toggleLoading || getTokenLoading);

    useEffect(() => {
        if (previousValueRef.current === value) {
            // no changes
            return;
        }

        (async () => {
            if (value) {
                const checkedBiometric = await checkBiometric();

                // check with flutter first
                if (!checkedBiometric) {
                    // set it back to false
                    previousValueRef.current = false;
                    setFieldValue('biometric', false);

                    return;
                }
            } else {
                // remove token from local storage
                localStorage.removeItem('singleSignIn');
            }

            // update the remote setting
            await toggleBiometric({ variables: { deviceFingerprint, state: value } });

            if (value) {
                // get the token
                const response = await getToken({ variables: { deviceFingerprint } });
                const token = response.data.getSingleSignInToken;

                // update local storage
                localStorage.setItem('singleSignIn', token);
            } else {
                // remove token from local storage
                localStorage.removeItem('singleSignIn');
            }

            // update previous value
            previousValueRef.current = value;

            // display success message
            show('success', t('settingsPage:alerts.saveSuccessful'));
        })();
    }, [t, show, value, deviceFingerprint, checkBiometric, previousValueRef, setFieldValue, toggleBiometric, getToken]);
};

const usePush = () => {
    const { t } = useTranslation(['settingsPage']);
    const { deviceFingerPrint: deviceFingerprint, initializePush } = useFlutter();
    const { show } = useAlert();

    // get values from formik
    const { values, setFieldValue } = useFormikContext<FormValues>();
    const { pushNotification: value } = values;

    // get mutations
    const [enablePush, { loading: enableLoading }] = useEnablePushMutation();
    const [disablePush, { loading: disableLoading }] = useDisablePushMutation();

    useLoadingController(enableLoading || disableLoading);

    // track latest values
    const previousValueRef = useRef(value);

    useEffect(() => {
        if (previousValueRef.current === value) {
            // no changes
            return;
        }

        (async () => {
            if (value) {
                // check with flutter first to get the registration token
                const { registrationToken } = await initializePush();

                if (!registrationToken) {
                    // set it back to false
                    previousValueRef.current = false;
                    setFieldValue('pushNotification', false);

                    return;
                }

                // call the API
                await enablePush({ variables: { registrationToken, deviceFingerprint } });
            } else {
                await disablePush({ variables: { deviceFingerprint } });
            }

            // update previous value
            previousValueRef.current = value;

            // display success message
            show('success', t('settingsPage:alerts.saveSuccessful'));
        })();
    }, [t, show, value, deviceFingerprint, previousValueRef, setFieldValue, enablePush, disablePush, initializePush]);
};

const useMessageTemplateUseCases = () => {
    const { t } = useTranslation(['settingsPage']);
    const { show } = useAlert();

    const { loading: initialLoading } = useGetMessageTemplateSettingsQuery();

    const { values } = useFormikContext<FormValues>();
    const {
        biddingOpenedTemplateUseCase,
        bidSubmissionTemplateUseCase,
        noChancesLeftTemplateUseCase,
        bidAwardedTemplateUseCase,
        otpTemplateUseCase,
        userAccountRejectionTemplateUseCase,
        userAccountApprovalTemplateUseCase,
    } = values;

    const [updateMessageTemplateSettings, { loading }] = useUpdateMessageTemplateSettingsMutation();

    useLoadingController(loading);

    const previousValueRef = useRef({
        biddingOpenedTemplateUseCase,
        bidSubmissionTemplateUseCase,
        noChancesLeftTemplateUseCase,
        bidAwardedTemplateUseCase,
        otpTemplateUseCase,
        userAccountRejectionTemplateUseCase,
        userAccountApprovalTemplateUseCase,
    });

    const readyRef = useRef(false);

    useEffect(() => {
        if (!readyRef.current) {
            if (initialLoading) {
                return;
            }

            readyRef.current = true;
            previousValueRef.current = {
                biddingOpenedTemplateUseCase,
                bidSubmissionTemplateUseCase,
                noChancesLeftTemplateUseCase,
                bidAwardedTemplateUseCase,
                otpTemplateUseCase,
                userAccountRejectionTemplateUseCase,
                userAccountApprovalTemplateUseCase,
            };

            return;
        }

        const previous = previousValueRef.current;
        const hasChanged =
            previous.biddingOpenedTemplateUseCase !== biddingOpenedTemplateUseCase ||
            previous.bidSubmissionTemplateUseCase !== bidSubmissionTemplateUseCase ||
            previous.noChancesLeftTemplateUseCase !== noChancesLeftTemplateUseCase ||
            previous.bidAwardedTemplateUseCase !== bidAwardedTemplateUseCase ||
            previous.otpTemplateUseCase !== otpTemplateUseCase ||
            previous.userAccountRejectionTemplateUseCase !== userAccountRejectionTemplateUseCase ||
            previous.userAccountApprovalTemplateUseCase !== userAccountApprovalTemplateUseCase;

        if (!hasChanged) {
            return;
        }

        (async () => {
            await updateMessageTemplateSettings({
                variables: {
                    useCaseSettings: [
                        {
                            useCase: TemplateUseCases.BiddingOpened,
                            selectedTemplateSid: biddingOpenedTemplateUseCase || null,
                        },
                        {
                            useCase: TemplateUseCases.BidSubmission,
                            selectedTemplateSid: bidSubmissionTemplateUseCase || null,
                        },
                        {
                            useCase: TemplateUseCases.NoChancesLeft,
                            selectedTemplateSid: noChancesLeftTemplateUseCase || null,
                        },
                        {
                            useCase: TemplateUseCases.BidAwarded,
                            selectedTemplateSid: bidAwardedTemplateUseCase || null,
                        },
                        {
                            useCase: TemplateUseCases.Otp,
                            selectedTemplateSid: otpTemplateUseCase || null,
                        },
                        {
                            useCase: TemplateUseCases.UserAccountRejection,
                            selectedTemplateSid: userAccountRejectionTemplateUseCase || null,
                        },
                        {
                            useCase: TemplateUseCases.UserAccountApproval,
                            selectedTemplateSid: userAccountApprovalTemplateUseCase || null,
                        },
                    ],
                },
                refetchQueries: [{ query: GetMessageTemplateSettingsDocument }],
            });

            previousValueRef.current = {
                biddingOpenedTemplateUseCase,
                bidSubmissionTemplateUseCase,
                noChancesLeftTemplateUseCase,
                bidAwardedTemplateUseCase,
                otpTemplateUseCase,
                userAccountRejectionTemplateUseCase,
                userAccountApprovalTemplateUseCase,
            };

            show('success', t('settingsPage:alerts.saveSuccessful'));
        })();
    }, [
        t,
        show,
        initialLoading,
        biddingOpenedTemplateUseCase,
        bidSubmissionTemplateUseCase,
        noChancesLeftTemplateUseCase,
        bidAwardedTemplateUseCase,
        otpTemplateUseCase,
        userAccountRejectionTemplateUseCase,
        userAccountApprovalTemplateUseCase,
        updateMessageTemplateSettings,
    ]);
};

const SettingsUpdates = () => {
    useBiometric();
    usePush();
    useMessageTemplateUseCases();

    return null;
};

export default SettingsUpdates;
